import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import * as FaIconsBrands from '@fortawesome/free-brands-svg-icons'
import * as FaIconsRegular from '@fortawesome/pro-regular-svg-icons'
import * as FaIconsSolid from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import CustomIcons from './customIcons'
import {
  FontAwesomeIconKeyBrand,
  FontAwesomeIconKeyBrandCapitalized,
  FontAwesomeIconKeyCapitalized,
  FontAwesomeIconKeyRegular,
  FontAwesomeIconKeysBrand,
  FontAwesomeIconKeysRegular,
  FontAwesomeIconKeySolid,
  FontAwesomeIconKeySolidCapitalized,
  FontAwesomeIconKeysSolid,
  getIconKeyFromString,
  IconKey,
  IconProps,
  CustomIconKey,
} from './util'

const FontAwesomeRegularIconDefinitions = FaIconsRegular as unknown as Record<
  FontAwesomeIconKeyRegular,
  IconDefinition
>

const FontAwesomeSolidIconDefinitions = FaIconsSolid as unknown as Record<
  FontAwesomeIconKeySolid,
  IconDefinition
>

const FontAwesomeBrandIconDefinitions = FaIconsBrands as unknown as Record<
  FontAwesomeIconKeyBrand,
  IconDefinition
>

const getFontAwesomeIconDefinition = (iconKey: FontAwesomeIconKeyCapitalized) => {
  const lowerCasedIconKey = `f${iconKey.substring(1)}` as FontAwesomeIconKeyRegular
  return FontAwesomeRegularIconDefinitions[lowerCasedIconKey]
}

const FaRegularIcons: [FontAwesomeIconKeyCapitalized, React.FC<IconProps>][] =
  FontAwesomeIconKeysRegular.map((k) => {
    const iconKey = getIconKeyFromString(k)
    return [
      iconKey,
      (props: IconProps) => {
        return <FontAwesomeIcon {...props} icon={getFontAwesomeIconDefinition(iconKey)} />
      },
    ]
  })

const getFontAwesomeIconDefinitionSolid = (iconKey: FontAwesomeIconKeySolidCapitalized) => {
  const lowerCasedIconKey = iconKey.replace(/^F(.*)Solid$/, 'f$1') as FontAwesomeIconKeySolid
  return FontAwesomeSolidIconDefinitions[lowerCasedIconKey]
}

const FaSolidIcons: [FontAwesomeIconKeySolidCapitalized, React.FC<IconProps>][] =
  FontAwesomeIconKeysSolid.map((k: FontAwesomeIconKeySolid) => {
    const iconKey = `F${k.substring(1)}Solid` as FontAwesomeIconKeySolidCapitalized
    return [
      iconKey,
      (props: IconProps) => {
        return <FontAwesomeIcon {...props} icon={getFontAwesomeIconDefinitionSolid(iconKey)} />
      },
    ]
  })

const getFontAwesomeIconDefinitionBrand = (iconKey: FontAwesomeIconKeyBrandCapitalized) => {
  const lowerCasedIconKey = `f${iconKey.substring(1)}` as FontAwesomeIconKeyBrand
  return FontAwesomeBrandIconDefinitions[lowerCasedIconKey]
}

const FaBrandIcons: [FontAwesomeIconKeyBrandCapitalized, React.FC<IconProps>][] =
  FontAwesomeIconKeysBrand.map((k: FontAwesomeIconKeyBrand) => {
    const iconKey = getIconKeyFromString(k) as FontAwesomeIconKeyBrandCapitalized
    return [
      iconKey,
      (props: IconProps) => {
        return <FontAwesomeIcon {...props} icon={getFontAwesomeIconDefinitionBrand(iconKey)} />
      },
    ]
  })

type InternalIconProps = {
  className?: string
}
const AllIconsByIconKey = Object.fromEntries([
  ...Object.entries(CustomIcons),
  ...FaRegularIcons,
  ...FaSolidIcons,
  ...FaBrandIcons,
]) as Record<IconKey, React.FC<InternalIconProps>>

const IconBase = ({ className, icon }: IconProps) => {
  // While this null check isn't technically required by the typing, it safeguards against icon keys that were incorrectly casted from strings
  const IconComponent: React.FC<InternalIconProps> | null =
    AllIconsByIconKey[icon] ?? CustomIcons[icon as CustomIconKey] ?? null
  if (!IconComponent) {
    return null
  }
  return <IconComponent className={className} />
}

const Icon = Object.assign(IconBase, AllIconsByIconKey)

export default Icon
